import {useCallback, useEffect, useRef, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router';
import {
  meQueryOptions,
  useExchangeLoginToken,
  useStartLoginTicket,
  useWebAppLogin,
  waitForTicketApproval,
} from '../api/auth';
import {ApiError} from '../api/client';
import {FullScreenLoader} from '../components/FullScreenLoader';

type TgState = 'checking' | 'has-init-data' | 'no-init-data';

type BotLoginStatus = 'idle' | 'waiting' | 'expired' | 'error';

type MagicLinkStatus = 'idle' | 'pending' | 'rejected' | 'error';

function readAndStripLoginToken(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token) return null;
  params.delete('token');
  const qs = params.toString();
  const newUrl =
    window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
  window.history.replaceState({}, '', newUrl);
  return token;
}

export function Landing() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery(meQueryOptions);
  const webAppLogin = useWebAppLogin();
  const startTicket = useStartLoginTicket();
  const exchangeToken = useExchangeLoginToken();
  const [tgState, setTgState] = useState<TgState>('checking');
  const [initData, setInitData] = useState<string>('');
  const [botLogin, setBotLogin] = useState<BotLoginStatus>('idle');
  const [magicLink, setMagicLink] = useState<MagicLinkStatus>('idle');
  const tokenRef = useRef<string | null | undefined>(undefined);
  const waitAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => waitAbortRef.current?.abort();
  }, []);

  if (tokenRef.current === undefined) {
    tokenRef.current = readAndStripLoginToken();
  }

  useEffect(() => {
    if (tgState === 'checking') return;
    if (tgState === 'has-init-data') return;
    if (me.data) return;
    if (exchangeToken.isPending || exchangeToken.isSuccess) return;
    const token = tokenRef.current;
    if (!token) return;
    tokenRef.current = null;
    setMagicLink('pending');
    exchangeToken.mutate(token, {
      // Зберігаємо решту query (anketaId та інше) — бот може передати ?token=X&anketaId=Y,
      // а Dashboard на /app очікує anketaId щоб показати Dropzone.
      onSuccess: () => navigate(`/app${window.location.search}`, {replace: true}),
      onError: (err) => {
        if (err instanceof ApiError && err.status === 403) {
          setMagicLink('rejected');
        } else {
          setMagicLink('error');
        }
      },
    });
  }, [tgState, me.data, exchangeToken, navigate]);

  const handleBotLogin = useCallback(() => {
    setBotLogin('waiting');
    startTicket.mutate(undefined, {
      onSuccess: ({ticket, botDeepLink}) => {
        window.open(botDeepLink, '_blank', 'noopener,noreferrer');
        const ctrl = new AbortController();
        waitAbortRef.current?.abort();
        waitAbortRef.current = ctrl;
        waitForTicketApproval(ticket, ctrl.signal)
          .then((user) => {
            qc.setQueryData(meQueryOptions.queryKey, user);
            navigate(`/app${window.location.search}`, {replace: true});
          })
          .catch((err) => {
            if (ctrl.signal.aborted) return;
            if (err instanceof ApiError && err.status === 410) {
              setBotLogin('expired');
            } else {
              setBotLogin('error');
            }
          });
      },
      onError: () => setBotLogin('error'),
    });
  }, [startTicket, qc, navigate]);

  useEffect(() => {
    if (me.data) {
      navigate(`/app${window.location.search}`, {replace: true});
    }
  }, [me.data, navigate]);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) {
      setTgState('no-init-data');
      return;
    }
    try {
      tg.ready();
      tg.expand();
    } catch {
      // SDK is loaded but ready/expand might throw outside Telegram client — ignore
    }
    if (tg.initData && tg.initData.length > 0) {
      setInitData(tg.initData);
      setTgState('has-init-data');
    } else {
      setTgState('no-init-data');
    }
  }, []);

  useEffect(() => {
    if (tgState !== 'has-init-data') return;
    if (me.data) return;
    if (webAppLogin.isPending || webAppLogin.isSuccess) return;
    webAppLogin.mutate(initData, {
      onSuccess: () => navigate(`/app${window.location.search}`, {replace: true}),
    });
  }, [tgState, initData, me.data, webAppLogin, navigate]);

  if (me.isPending) {
    return <FullScreenLoader />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight">Drive Uploader</h1>
        </div>

        {tgState === 'checking' && (
          <p className="text-slate-500 text-sm">Проверяем Telegram…</p>
        )}

        {tgState === 'has-init-data' && webAppLogin.isPending && (
          <p className="text-slate-700 text-sm">Вход…</p>
        )}

        {webAppLogin.isError && (
          <p className="text-red-600 text-sm">
            Доступ запрещён. Возможно, ты ещё не зарегистрирован — напиши /start боту.
          </p>
        )}

        {magicLink === 'pending' && (
          <p className="text-slate-700 text-sm">Входим…</p>
        )}

        {magicLink === 'rejected' && (
          <p className="text-red-600 text-sm">
            Ссылка для входа недействительна. Открой бота, нажми /start и используй свежую кнопку.
          </p>
        )}

        {magicLink === 'error' && (
          <p className="text-red-600 text-sm">
            Не удалось войти по ссылке. Попробуй ещё раз через минуту.
          </p>
        )}

        {tgState === 'no-init-data' && magicLink === 'idle' && (
          <div className="space-y-3 rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-700">Войти через бота в Telegram:</p>
            <button
              type="button"
              onClick={handleBotLogin}
              disabled={botLogin === 'waiting' || startTicket.isPending}
              className="w-full rounded bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium px-4 py-2 disabled:opacity-60"
            >
              {botLogin === 'waiting'
                ? 'Ждём подтверждения в боте…'
                : 'Войти через Telegram-бота'}
            </button>
            {botLogin === 'waiting' && (
              <p className="text-xs text-slate-500">
                В новой вкладке открылся бот. Нажми «Start» — и эта страница сама залогинит тебя.
              </p>
            )}
            {botLogin === 'expired' && (
              <p className="text-xs text-red-600">
                Ссылка устарела. Нажми кнопку ещё раз.
              </p>
            )}
            {botLogin === 'error' && (
              <p className="text-xs text-red-600">
                Не удалось начать вход. Попробуй ещё раз.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
